<template>
  <div class="tc-fd-form" data-testid="freshdesk-connect-form">
    <div class="tc-fd-form-fields">
      <div class="mb-3">
        <label :for="domainId" class="form-label tc-fd-label">Freshdesk domain</label>
        <input
          :id="domainId"
          v-model="domain"
          type="text"
          class="form-control form-control-sm"
          placeholder="yourco.freshdesk.com"
          autocomplete="off"
          :disabled="isPending"
          data-testid="freshdesk-domain-input"
        />
      </div>
      <div class="mb-2">
        <label :for="keyId" class="form-label tc-fd-label">API key</label>
        <div class="input-group input-group-sm">
          <input
            :id="keyId"
            v-model="apiKey"
            :type="showKey ? 'text' : 'password'"
            class="form-control"
            autocomplete="off"
            :disabled="isPending"
            data-testid="freshdesk-apikey-input"
          />
          <button
            type="button"
            class="btn btn-outline-secondary"
            :aria-label="showKey ? 'Hide API key' : 'Show API key'"
            :disabled="isPending"
            data-testid="freshdesk-reveal-btn"
            @click="showKey = !showKey"
          >{{ showKey ? 'Hide' : 'Show' }}</button>
        </div>
        <div class="tc-fd-hint">Profile settings &rarr; View API key</div>
      </div>
      <div
        v-if="fieldError"
        class="tc-fd-error"
        role="alert"
        data-testid="freshdesk-field-error"
      >{{ fieldError }}</div>
    </div>
    <div class="tc-fd-actions">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="isPending"
        data-testid="freshdesk-cancel-btn"
        @click="emit('cancel')"
      >Cancel</button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="isPending || !canSubmit"
        data-testid="freshdesk-connect-btn"
        @click="handleConnect"
      >
        <span
          v-if="isPending"
          class="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        ></span>
        Connect
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { connectFreshdesk } from '../api/sources';
import { ApiClientError } from '../api/client';
import { useSourcesStore } from '../stores/sources';

const emit = defineEmits<{
  cancel: [];
  connected: [agentName: string];
}>();

const queryClient = useQueryClient();
const sourcesStore = useSourcesStore();

const domainId = 'fd-domain-' + Math.random().toString(36).slice(2, 7);
const keyId    = 'fd-key-'    + Math.random().toString(36).slice(2, 7);

const domain   = ref('');
const apiKey   = ref('');
const showKey  = ref(false);
const fieldError = ref<string | null>(null);

const canSubmit = computed(() => domain.value.trim().length > 0 && apiKey.value.trim().length > 0);

const connectMutation = useMutation({
  mutationFn: () => connectFreshdesk(domain.value.trim(), apiKey.value.trim()),
  onSuccess: (data) => {
    sourcesStore.noteSourceConnected(queryClient);
    sourcesStore.addToast('success', `Connected to Freshdesk as ${data.agentName}`);
    emit('connected', data.agentName);
  },
  onError: (err) => {
    if (err instanceof ApiClientError) {
      fieldError.value = err.message;
      if (err.code === 'freshdesk_rejected') {
        apiKey.value = '';
      }
    } else {
      fieldError.value = 'An unexpected error occurred.';
    }
  },
});

const isPending = computed(() => connectMutation.isPending.value);

function handleConnect(): void {
  fieldError.value = null;
  if (!canSubmit.value) return;
  connectMutation.mutate();
}
</script>
